import React from "react";
import { useTranslation } from "react-i18next";
import { CustomButton } from "../../../atoms/CustomButton";
import type { AgendaType } from "../../../../hooks/agenda";

interface AgendaSetupGuideProps {
  types: AgendaType[];
  onCreateType: () => void;
}

export const readyType = (types: AgendaType[]) =>
  types.find((type) => type.active && type.members.length > 0);

/** Shown until an active type offers someone; the link step has nothing to tick. */
const AgendaSetupGuide: React.FC<AgendaSetupGuideProps> = ({
  types,
  onCreateType,
}) => {
  const { t } = useTranslation();
  if (readyType(types)) return null;

  const steps: {
    title: string;
    text: string;
    action?: React.ReactNode;
  }[] = [
    {
      title: t("agenda.guide.typeTitle"),
      text: t("agenda.guide.typeText"),
      action: (
        <CustomButton
          variant="primary"
          size="sm"
          title={t("agenda.guide.typeAction") as string}
          onClick={onCreateType}
        />
      ),
    },
    {
      title: t("agenda.guide.linkTitle"),
      text: t("agenda.guide.linkText"),
    },
  ];

  return (
    <section
      aria-labelledby="agenda-setup-guide-title"
      className="mb-4 rounded-lg border border-gray-200 bg-white"
    >
      <div className="border-b border-gray-100 px-4 py-3">
        <h2
          id="agenda-setup-guide-title"
          className="text-base font-semibold text-gray-900"
        >
          {t("agenda.guide.title")}
        </h2>
        <p className="mt-0.5 text-xs text-gray-500">
          {t("agenda.guide.subtitle")}
        </p>
      </div>
      <ol className="divide-y divide-gray-100">
        {steps.map((step, index) => (
          <li
            key={step.title}
            className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:flex-wrap sm:items-center"
          >
            <div className="flex min-w-0 flex-1 items-start gap-3 sm:basis-64">
              <span
                aria-hidden="true"
                className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gray-100 text-xs font-semibold tabular-nums text-gray-700"
              >
                {index + 1}
              </span>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-gray-900">
                  {step.title}
                </p>
                <p className="text-xs text-gray-500">{step.text}</p>
              </div>
            </div>
            {step.action && (
              <div className="flex flex-wrap items-center gap-2 pl-9 sm:pl-0">
                {step.action}
              </div>
            )}
          </li>
        ))}
      </ol>
    </section>
  );
};

export default AgendaSetupGuide;
